import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../../components/admin/AdminLayout';
import CmsTabs from '../../../components/admin/CmsTabs';
import { formatApiError } from '../../../lib/apiError';
import axios from 'axios';
import { Settings as SettingsIcon, Loader2, Save, CheckCircle2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Toggle = ({ checked, onChange, testid }) => (
  <button type="button" onClick={() => onChange(!checked)} data-testid={testid}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-amber-500' : 'bg-slate-600'}`}>
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

const CmsSettings = () => {
  const [form, setForm] = useState({ default_locale: 'en', preview_enabled: true, versioning_enabled: true, default_page_size: 10 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // {type, msg}

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/cms/settings`);
      setForm({
        default_locale: r.data.default_locale,
        preview_enabled: r.data.preview_enabled,
        versioning_enabled: r.data.versioning_enabled,
        default_page_size: r.data.default_page_size,
      });
    } catch (e) { console.error('Error loading CMS settings:', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true); setFeedback(null);
    try {
      await axios.put(`${API_URL}/api/admin/cms/settings`, {
        default_locale: form.default_locale,
        preview_enabled: form.preview_enabled,
        versioning_enabled: form.versioning_enabled,
        default_page_size: Number(form.default_page_size) || 10,
      });
      setFeedback({ type: 'success', msg: 'CMS settings saved.' });
    } catch (e) {
      setFeedback({ type: 'error', msg: formatApiError(e, 'Failed to save settings') });
    } finally { setSaving(false); }
  };

  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-2" data-testid="cms-settings-title">
          <SettingsIcon className="w-6 h-6 text-amber-400" /> CMS
        </h1>
        <p className="text-slate-400">Configure CMS behaviour. These settings are internal — no public effect.</p>
      </div>

      <CmsTabs />

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>
      ) : (
        <div className="max-w-2xl">
          {feedback && (
            <div className={`flex items-center gap-2 p-3 mb-4 rounded-lg text-sm border ${feedback.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`} data-testid="cms-settings-feedback">
              {feedback.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              {feedback.msg}
            </div>
          )}

          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl divide-y divide-slate-700/50">
            <div className="flex items-center justify-between p-5">
              <div>
                <p className="text-white font-medium">Default Language</p>
                <p className="text-sm text-slate-400">Default locale for new content.</p>
              </div>
              <input type="text" value={form.default_locale} onChange={(e) => setForm({ ...form, default_locale: e.target.value })}
                data-testid="cms-settings-default-locale"
                className="w-24 px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-center focus:outline-none focus:border-amber-500" />
            </div>

            <div className="flex items-center justify-between p-5">
              <div>
                <p className="text-white font-medium">Enable Preview Mode</p>
                <p className="text-sm text-slate-400">Allow previewing draft content in the admin.</p>
              </div>
              <Toggle checked={form.preview_enabled} onChange={(v) => setForm({ ...form, preview_enabled: v })} testid="cms-settings-preview-toggle" />
            </div>

            <div className="flex items-center justify-between p-5">
              <div>
                <p className="text-white font-medium">Enable Versioning</p>
                <p className="text-sm text-slate-400">Track version increments on publish.</p>
              </div>
              <Toggle checked={form.versioning_enabled} onChange={(v) => setForm({ ...form, versioning_enabled: v })} testid="cms-settings-versioning-toggle" />
            </div>

            <div className="flex items-center justify-between p-5">
              <div>
                <p className="text-white font-medium">Default Page Size</p>
                <p className="text-sm text-slate-400">Rows per page in CMS listings.</p>
              </div>
              <input type="number" min={1} max={200} value={form.default_page_size} onChange={(e) => setForm({ ...form, default_page_size: e.target.value })}
                data-testid="cms-settings-page-size"
                className="w-24 px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-center focus:outline-none focus:border-amber-500" />
            </div>
          </div>

          <div className="flex justify-end mt-5">
            <button onClick={handleSave} disabled={saving} data-testid="cms-settings-save-btn"
              className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50 transition-colors">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> Save Settings</>}
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default CmsSettings;
